import type { GoalZoneDoctorSummary } from "@care-by-ella/contracts";

// TES-998 (Ö-238) — the goal zone as the doctor needs to read it.
//
// Emelie 2026-09-29: the patient's choice to keep her weight is a wish, and
// "Kirill fastställer eventuell viktstabilisering vid nästa lämpliga"
// "medicinska bedömning" — normally the monthly review. So the wish is
// shown where that review happens (the patient header, on every tab, and
// above the monthly review itself), and it says plainly that nothing has
// changed phase. Her F-02 "Ja, redan nått" (intake v10–v15) is shown the
// same way: "som patientuppgift". From v16 F-02B asks instead whether her
// current weight is within her goal (TES-1162); that answer is shown in all
// four forms, with the day she gave it.
//
// Returns the lines to show, or an empty list. A pure function so the wording
// is testable without rendering.
export function goalZoneLines(
  summary: GoalZoneDoctorSummary | null | undefined,
): string[] {
  if (!summary) return [];
  const lines: string[] = [];
  const c = summary.confirmation;
  const day = (iso: string | null) => (iso ? iso.slice(0, 10) : "");
  if (c) {
    const reached = `Vikten ${c.triggerKg} kg (${c.triggerMeasuredOn}) nådde målvikten ${c.goalWeightKg} kg`;
    switch (c.status) {
      case "awaiting_choice":
        lines.push(`${reached}. Patienten har inte valt ännu.`);
        break;
      case "thinking":
        lines.push(`${reached}. Patienten vill fundera (${day(c.choiceAt)}).`);
        break;
      case "keep_requested":
        lines.push(
          `Patienten önskar behålla ungefär den vikt hon har nu (${day(c.choiceAt)}). Önskemål inför månadsbedömningen – fasen är inte ändrad.`,
        );
        break;
      case "new_goal":
        lines.push(
          `${reached}. Patienten valde att sätta ett nytt viktmål (${day(c.choiceAt)}).`,
        );
        break;
      default:
        // withdrawn / closed: nothing open for the doctor to act on.
        break;
    }
  }
  const answeredOn = summary.intakeSubmittedAt
    ? ` (${day(summary.intakeSubmittedAt)})`
    : "";
  const within = summary.intakeWeightWithinGoal;
  if (within) {
    // TES-1162 — F-02B's goal status: every answer is shown, and none of
    // them sets the phase; the doctor chooses it.
    lines.push(
      `Patientuppgift i hälsodeklarationen${answeredOn}: ${WITHIN_GOAL[within]} Fasen väljs av läkaren.`,
    );
  } else if (summary.intakeGoalAlreadyReached === "ja") {
    lines.push(
      `Patientuppgift i hälsodeklarationen${answeredOn}: har redan nått sin målvikt eller målzon.`,
    );
  }
  return lines;
}

const WITHIN_GOAL: Record<
  NonNullable<GoalZoneDoctorSummary["intakeWeightWithinGoal"]>,
  string
> = {
  ja: "nuvarande vikt ligger inom målvikten eller målzonen.",
  nej: "nuvarande vikt ligger inte inom målvikten eller målzonen.",
  osaker: "osäker på om nuvarande vikt ligger inom målvikten eller målzonen.",
  ingen_malvikt: "har ingen målvikt eller målzon.",
};
